import BaseAPIService from './BaseAPIService'
import {
  ContactLoginDTO,
  ContactRegisterDTO,
  ContactSearchDTO,
  ContactNewPasswordDTO,
  ContactResetPasswordDTO,
  ContactLoginResponseDTO,
  ContactRegisterResponseDTO,
  ContactSearchResponseDTO,
  ContactNewPasswordResponseDTO,
  ContactResetPasswordResponseDTO,
  ContactRefreshResponseDTO,
  ContactTokens,
  UserInfo,
  UpdateUserInfoDTO,
} from './AuthService.interface'
import axios from 'axios'
import Cookies from 'js-cookie'
import endpoints from '@/config/endpoints'

import { CookieName } from '@/config/cookies'
import { LocaleStorageKey } from '@/config/localeStorageKey'

import { jwtDecode } from 'jwt-decode'

// Store
import store from '@/store'
import { normalizeLocale } from '@/utils'
import { DomainEnum } from '@/config'

class AuthService extends BaseAPIService {
  private cookieNameList = [
    CookieName.USER_ACCESS_TOKEN,
    CookieName.USER_ID_TOKEN,
    CookieName.REFRESH_TOKEN,
  ]

  constructor() {
    super(endpoints.auth) // Use "auth" key from endpoints
  }

  /**
   * Log in a user and save tokens in cookies.
   */
  async login(dto: ContactLoginDTO): Promise<ContactLoginResponseDTO | null> {
    const locale = this.getLegacyLocale()
    try {
      const url = `/contact/login`

      const { data } = await this.apiClient.post<ContactLoginResponseDTO>(
        url,
        dto,
        {
          headers:{...this.getDefaultHeaders(), locale},
          params: this.getDefaultParams(),
        }
      )

      // Save tokens in cookies
      this.setTokensInCookies(data.tokens)
      this.setUserId(data.userInfo.iresa_id)

      if (window.env.VUE_APP_LOCALE === 'fr-FR-CSE') {
        this.setCompanyInCookies(data.company)
      }

      return data
    } catch (err) {
      console.error('Login failed:', err)
      return null
    }
  }

  /**
   * Register a new user.
   */
  async register(
    dto: ContactRegisterDTO
  ): Promise<ContactRegisterResponseDTO | null> {
    const locale = this.getLegacyLocale()

    try {
      const url = `/contact`
      const { data } = await this.apiClient.post<ContactRegisterResponseDTO>(
        url,
        dto,
        { headers: {...this.getDefaultHeaders(), locale}, params: this.getDefaultParams() }
      )

      // Save tokens in cookies
      this.setTokensInCookies(data.tokens)
      this.setUserId(data.userInfo.iresa_id)

      return data
    } catch (err) {
      console.error('Registration failed:', err)
      if (axios.isAxiosError(err) && err.response?.data) {
        const apiMessage = (err.response.data as { message?: string }).message
        throw new Error(apiMessage || err.message)
      }
      throw err
    }
  }

  /**
   * Search for a contact by email.
   */
  async search(
    dto: ContactSearchDTO
  ): Promise<ContactSearchResponseDTO | null> {
    try {
      const url = `/contact`

      const locale = this.getLegacyLocale()

      const { data } = await this.apiClient.get<ContactSearchResponseDTO>(url, {
        params: { email: dto.email, ...this.getDefaultParams() },
        headers: {...this.getDefaultHeaders(), locale},
      })
      return data
    } catch (err) {
      console.error('Search failed:', err)
      return null
    }
  }

  /**
   * Reset the user's password.
   */
  async resetPassword(
    dto: ContactResetPasswordDTO
  ): Promise<ContactResetPasswordResponseDTO | null> {
    try {
      const url = `/contact/forgot-password`

      // Get the locale from the store, changes based on company lang
      const locale = normalizeLocale(store.getters.getLocale)

      const headers = {
        ...this.getDefaultHeaders(),
        locale
      }
      

      const { data } =
        await this.apiClient.post<ContactResetPasswordResponseDTO>(url, dto, {
          headers,
          params: this.getDefaultParams()
        })
      return data
    } catch (err) {
      console.error('Reset password failed:', err)
      return null
    }
  }

  /**
   * Set a new password for the user.
   */
  async newPassword(
    dto: ContactNewPasswordDTO
  ): Promise<ContactNewPasswordResponseDTO | null> {
    try {
      const url = `/contact/forgot-password/reset`
      const locale = this.getLegacyLocale()

      const { data } = await this.apiClient.post<ContactNewPasswordResponseDTO>(
        url,
        dto,
        { headers: {...this.getDefaultHeaders(), locale}, params: this.getDefaultParams() }
      )
      return data
    } catch (err) {
      console.error('New password failed:', err)
      return null
    }
  }

  /**
   * Refresh the user's tokens.
   */
  async refresh(): Promise<UserInfo | null> {
    if (window.location.hostname !== DomainEnum.FR_CSE) {
      this.removeCompanyCookie()
    }

    const storedUserId = this.getUserId()
    if (!storedUserId) {
      console.warn('User ID missing in storage and cookies')
      return null // Handle as a logged-out user
    }

    try {
      const url = `/contact/refresh`
      const locale = this.getLegacyLocale()
      
      const { data } = await this.apiClient.get<ContactRefreshResponseDTO>(
        url,
        {
          headers: {
            ...this.getDefaultHeaders(),
            locale,
            Authorization: Cookies.get(CookieName.REFRESH_TOKEN) || '',
          },
          params: {
            ...this.getDefaultParams(),
            iresaUserId: storedUserId,
          },
        }
      )

      // Save tokens in cookies
      this.setTokensInCookies(data.tokens)
      this.setCompanyInCookies(data.userInfo.company)
      this.setUserId(data.userInfo.iresa_id)

      return data.userInfo
    } catch (err) {
      console.error('Token refresh failed:', err)
      return null
    }
  }

  /**
   * Log out the user.
   */
  async logout(): Promise<void> {
    try {
      this.removeTokens()
    } catch (err) {
      console.error('logout failed:', err)
    }
  }

  /**
   * Update user info
   */
  async updateUserInfo(updates: UpdateUserInfoDTO): Promise<UserInfo> {
    try {
      this.validateTokens()

      const url = `/contact`
      const { data } = await this.apiClient.put<UserInfo>(url, updates, {
        headers: {
          ...this.getDefaultHeaders(),
          Authorization: Cookies.get(CookieName.USER_ID_TOKEN) || '',
        },
        params: this.getDefaultParams(),
      })

      // Return the updated data
      return data
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  /**
   * Change the user's password.
   */
  async changePassword(dto: {
    oldPassword: string
    password: string
  }): Promise<void> {
    try {
      this.validateTokens()
      const url = `/contact/password`

      // Retrieve tokens from cookies
      const accessToken = Cookies.get(CookieName.USER_ACCESS_TOKEN)
      const idToken = Cookies.get(CookieName.USER_ID_TOKEN)

      if (!accessToken || !idToken) {
        throw new Error('Access token or ID token is missing from cookies.')
      }

      // Include accessToken in the payload and idToken in the headers
      const payload = { ...dto, accessToken }

      await this.apiClient.put<void>(url, payload, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        params: this.getDefaultParams(),
      })

    } catch (err) {
      console.error('Password change failed:', err)
      throw err // Rethrow to handle it in the caller
    }
  }

  /**
   * Save tokens in cookies.
   */
  private setTokensInCookies(tokens: ContactTokens): void {
    Cookies.set(CookieName.USER_ID_TOKEN, tokens.idToken)
    Cookies.set(CookieName.USER_ACCESS_TOKEN, tokens.accessToken)
    Cookies.set(CookieName.REFRESH_TOKEN, tokens.refreshToken)
  }

  private setCompanyInCookies(company?: string | null) {
    Cookies.set(CookieName.COMPANY, company || '')
  }

  private removeCompanyCookie(): void {
    Cookies.remove(CookieName.COMPANY)
  }

  private setUserId(userId: string): void {
    localStorage.setItem(LocaleStorageKey.USER_ID, userId)
    Cookies.set(CookieName.USER_ID, userId) // Backup in cookies
  }

  private getUserId(): string | null {
    return (
      localStorage.getItem(LocaleStorageKey.USER_ID) ||
      Cookies.get(CookieName.USER_ID) ||
      null
    )
  }
  /**
   * Remove all tokens from cookies.
   */
  private removeTokens(): void {
    this.cookieNameList.forEach((cookieName: CookieName) => {
      Cookies.remove(cookieName)
    })
  }

  /**
   * Check that the user has all the required auth tokens.
   */
  public validateTokens(): void {
    this.cookieNameList.forEach((cookieName: CookieName) => {
      const cookieValue = Cookies.get(cookieName)

      if (!cookieValue) {
        throw new Error(`Missing required tokens`)
      }

      if (
        cookieName === CookieName.USER_ID_TOKEN ||
        cookieName === CookieName.USER_ACCESS_TOKEN
      ) {
        const decoded = jwtDecode(cookieValue)
        // Validate token expiration
        this.validateTokenExpiry(decoded as { exp: number })
      }
    })
  }

  /**
   * Validate token expiration.
   * @param decodedToken - The decoded JWT token payload.
   */
  private validateTokenExpiry(decodedToken: { exp: number }): void {
    const currentTime = Math.floor(Date.now() / 1000) // Current time in seconds

    if (decodedToken.exp && currentTime > decodedToken.exp) {
      throw new Error(`Token is expired`)
    }
  }
}

export default AuthService
